/**
 * cryptoService.js
 * Chiffrement client-side SafeHeir — Web Crypto API natif pour toutes les
 * primitives. Seule dépendance externe : @scure/bip39 (génération et
 * validation de la seed de récupération, DEC-030).
 *
 * Architecture MK + AAD (double-wrap, DEC-030 / DEC-031) :
 *   - À signup : mkBytes = CSPRNG(32), enveloppés DEUX fois.
 *     Wrap #1 password : wrappingKey = PBKDF2(password),
 *     AAD "safeheir-mk-password-v1|" + user_id
 *     → (encrypted_mk, mk_iv, mk_salt) dans user_profiles.
 *     Wrap #2 seed : seedKey = PBKDF2(entropy BIP39),
 *     AAD "safeheir-mk-seed-v1|" + user_id
 *     → (encrypted_mk_seed, mk_seed_iv, mk_seed_salt) dans user_profiles.
 *   - À login : fetch user_profiles → unwrapMasterKey(aad) → CryptoKey en mémoire React Context.
 *   - Vault items chiffrés sous la MK avec AAD "safeheir-vault-item-v1|" + item_id.
 *   - L'AAD garantit le binding cryptographique au contexte (anti-substitution).
 *   - La MK ne transite jamais par localStorage ni sessionStorage.
 *   - La seed n'est jamais persistée : affichage écran uniquement (DEC-030).
 *
 * Standards :
 *   - AES-256-GCM (NIST FIPS 197 + SP 800-38D)
 *   - PBKDF2-SHA256 100k iter (RFC 2898). Argon2id (RFC 9106) prévu post-MVP — voir K14.
 *   - BIP39 24 mots, wordlist française (256 bits d'entropie).
 *
 * Transmission (PF-03, spec crypto v1.1 §3.1, §3.2, Annexe B) :
 *   - K_trans = CSPRNG(32) par bénéficiaire, jamais dérivée d'un code.
 *   - Enveloppe bénéficiaire chiffrée sous K_trans,
 *     AAD "safeheir-transmission-envelope-v1|" + envelope_id.
 *   - K_trans enveloppée sous la MK, AAD "safeheir-ktrans-mk-v1|" + beneficiary_id.
 *   - Partage additif 2-sur-2 : part_serveur = CSPRNG(32),
 *     part_titulaire = K_trans XOR part_serveur, encodée en 24 mots BIP39 FR.
 */

import {
  entropyToMnemonic,
  generateMnemonic,
  mnemonicToEntropy,
  validateMnemonic,
} from '@scure/bip39';
import { wordlist } from '@scure/bip39/wordlists/french.js';

// ===== AAD helpers (Additional Authenticated Data) =====
// Format verrouillé DEC-017 + DEC-030. Ne JAMAIS modifier les chaînes
// sans plan de migration (divergence = coffre indéchiffrable).
// Garde stricte : argument non-string ou vide → throw immédiat
// (évite le binding cryptographique dégénéré "AAD|undefined"
// identifié par security-review CP2).

function assertNonEmptyString(value, argName) {
  if (typeof value !== 'string' || value.length === 0) {
    throw new Error(`${argName} requis (chaîne non vide)`);
  }
}

/** @param {string} userId @returns {string} AAD du wrap #1 (password). */
export const AAD_MK_PASSWORD_V1 = (userId) => {
  assertNonEmptyString(userId, 'AAD_MK_PASSWORD_V1: userId');
  return `safeheir-mk-password-v1|${userId}`;
};

/** @param {string} userId @returns {string} AAD du wrap #2 (seed BIP39). */
export const AAD_MK_SEED_V1 = (userId) => {
  assertNonEmptyString(userId, 'AAD_MK_SEED_V1: userId');
  return `safeheir-mk-seed-v1|${userId}`;
};

/** @param {string} itemId @returns {string} AAD d'un item de coffre. */
export const AAD_VAULT_ITEM_V1 = (itemId) => {
  assertNonEmptyString(itemId, 'AAD_VAULT_ITEM_V1: itemId');
  return `safeheir-vault-item-v1|${itemId}`;
};

/**
 * @param {string} beneficiaryId
 * @returns {string} AAD de l'enveloppement de K_trans sous la MK (PF-03).
 */
export const AAD_KTRANS_MK_V1 = (beneficiaryId) => {
  assertNonEmptyString(beneficiaryId, 'AAD_KTRANS_MK_V1: beneficiaryId');
  return `safeheir-ktrans-mk-v1|${beneficiaryId}`;
};

/**
 * @param {string} envelopeId
 * @returns {string} AAD d'une enveloppe bénéficiaire chiffrée sous K_trans (PF-03).
 */
export const AAD_TRANSMISSION_ENVELOPE_V1 = (envelopeId) => {
  assertNonEmptyString(envelopeId, 'AAD_TRANSMISSION_ENVELOPE_V1: envelopeId');
  return `safeheir-transmission-envelope-v1|${envelopeId}`;
};

/**
 * @param {string} beneficiaryId
 * @returns {string} AAD du contact de confiance chiffré sous la MK (PF-03 CP3.2).
 */
export const AAD_TRUSTED_CONTACT_V1 = (beneficiaryId) => {
  assertNonEmptyString(beneficiaryId, 'AAD_TRUSTED_CONTACT_V1: beneficiaryId');
  return `safeheir-trusted-contact-v1|${beneficiaryId}`;
};

// --- Helpers ---

function bufToBase64(buf) {
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function base64ToBuf(b64) {
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

/**
 * Encode une chaîne UTF-8 en Uint8Array pour usage AAD.
 * @param {string} str
 * @returns {Uint8Array}
 */
function strToBuf(str) {
  return new TextEncoder().encode(str);
}

/**
 * AES-256-GCM sur octets bruts, IV 12 octets aléatoire, tag concaténé au
 * ciphertext. Cœur commun de encryptData() et encryptBytes().
 * @param {Uint8Array} plaintext
 * @param {CryptoKey} key
 * @param {string} aad - Déjà validé par l'appelant.
 * @returns {Promise<{ ciphertext: string, iv: string }>} base64.
 */
async function aesGcmEncrypt(plaintext, key, aad) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const cipherbuf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: strToBuf(aad) },
    key,
    plaintext
  );
  return {
    ciphertext: bufToBase64(cipherbuf),
    iv: bufToBase64(iv),
  };
}

/**
 * Inverse de aesGcmEncrypt(). Cœur commun de decryptData() et decryptBytes().
 * @param {string} ciphertextBase64
 * @param {string} ivBase64
 * @param {CryptoKey} key
 * @param {string} aad - Déjà validé par l'appelant.
 * @returns {Promise<Uint8Array>}
 */
async function aesGcmDecrypt(ciphertextBase64, ivBase64, key, aad) {
  const plainbuf = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: base64ToBuf(ivBase64), additionalData: strToBuf(aad) },
    key,
    base64ToBuf(ciphertextBase64)
  );
  return new Uint8Array(plainbuf);
}

/**
 * Vrai si value est un Uint8Array. Test par tag interne plutôt que
 * instanceof : reste fiable entre realms (jsdom / Node Web Crypto).
 * @param {*} value
 * @returns {boolean}
 */
function isUint8Array(value) {
  return Object.prototype.toString.call(value) === '[object Uint8Array]';
}

/**
 * Garde stricte : Uint8Array de 32 octets exactement.
 * @param {*} value
 * @param {string} argName
 */
function assertBytes32(value, argName) {
  if (!isUint8Array(value) || value.length !== 32) {
    throw new Error(`${argName} : 32 octets (Uint8Array) requis`);
  }
}

// --- Wrapping key (PBKDF2) ---

/**
 * Dérive la clé d'enveloppe (wrapping key) qui chiffre la Master Key.
 * NE PAS utiliser pour chiffrer directement des données vault.
 * @param {string} password - Mot de passe en clair.
 * @param {string|null} saltBase64 - Sel base64 existant, ou null pour en générer un.
 * @returns {Promise<{ key: CryptoKey, salt: string }>}
 */
export async function deriveWrappingKey(password, saltBase64 = null) {
  const saltBuf = saltBase64
    ? base64ToBuf(saltBase64)
    : crypto.getRandomValues(new Uint8Array(16));

  const rawKey = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );

  const key = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBuf,
      iterations: 100000,
      hash: 'SHA-256',
    },
    rawKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );

  return { key, salt: bufToBase64(saltBuf) };
}

// --- Master Key ---

/**
 * Génère une Master Key aléatoire 256 bits (raw bytes).
 * À appeler UNE FOIS à l'inscription utilisateur.
 * @returns {Uint8Array} 32 octets CSPRNG.
 */
export function generateMasterKey() {
  return crypto.getRandomValues(new Uint8Array(32));
}

/**
 * Chiffre la MK (raw bytes) avec la wrapping key.
 * @param {Uint8Array} mkBytes - MK 32 bytes issue de generateMasterKey().
 * @param {CryptoKey} wrappingKey - Issue de deriveWrappingKey().
 * @param {string} aad - Additional Authenticated Data, contexte cryptographique.
 *                      Format SafeHeir : "safeheir-mk-password-v1|" + user_id
 * @returns {Promise<{ ciphertext: string, iv: string }>} base64.
 */
export async function wrapMasterKey(mkBytes, wrappingKey, aad) {
  if (!aad || typeof aad !== 'string') {
    throw new Error('wrapMasterKey: aad parameter is required (string)');
  }
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const cipherbuf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv, additionalData: strToBuf(aad) },
    wrappingKey,
    mkBytes
  );
  return {
    ciphertext: bufToBase64(cipherbuf),
    iv: bufToBase64(iv),
  };
}

/**
 * Déchiffre la MK depuis sa forme chiffrée et l'importe en CryptoKey
 * non-extractable.
 * @param {string} encryptedMKBase64
 * @param {string} ivBase64
 * @param {CryptoKey} wrappingKey
 * @param {string} aad - DOIT être identique à celui passé à wrapMasterKey().
 * @returns {Promise<CryptoKey>} MK importée, non-extractable.
 */
export async function unwrapMasterKey(encryptedMKBase64, ivBase64, wrappingKey, aad) {
  if (!aad || typeof aad !== 'string') {
    throw new Error('unwrapMasterKey: aad parameter is required (string)');
  }
  const mkBytes = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: base64ToBuf(ivBase64), additionalData: strToBuf(aad) },
    wrappingKey,
    base64ToBuf(encryptedMKBase64)
  );
  return crypto.subtle.importKey(
    'raw',
    mkBytes,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

// ===== Seed BIP39 (recovery) — DEC-030 =====

/**
 * Génère une seed BIP39 de 24 mots (256 bits d'entropie) en français.
 * Standard équivalent AES-256. Wordlist verrouillée française.
 * Affichage écran uniquement : ne jamais persister la valeur retournée.
 * @returns {string} 24 mots séparés par des espaces.
 */
export function generateSeed() {
  return generateMnemonic(wordlist, 256);
}

/**
 * Dérive une clé AES-GCM 256 depuis une seed BIP39 française.
 * PBKDF2-SHA256 100k iter sur l'entropie brute de la seed (DEC-031 Q2),
 * cohérent avec deriveWrappingKey() qui dérive depuis le mot de passe.
 * Valide la seed (mots + checksum) avant toute dérivation.
 * @param {string} seedPhrase - 24 mots français séparés par des espaces.
 * @param {string|null} saltBase64 - Sel base64 existant, ou null pour en générer un.
 * @returns {Promise<{ key: CryptoKey, saltBase64: string }>} Clé non-extractable.
 * @throws {Error} Si la seed est invalide (mot hors wordlist ou checksum faux).
 */
export async function deriveFromSeed(seedPhrase, saltBase64 = null) {
  if (!validateMnemonic(seedPhrase, wordlist)) {
    throw new Error('Seed BIP39 invalide (mots ou checksum incorrects)');
  }

  // entropy = 32 octets bruts, utilisés comme matériel de clé PBKDF2
  // (équivalent du password de deriveWrappingKey, input binaire).
  const entropy = mnemonicToEntropy(seedPhrase, wordlist);
  const saltBuf = saltBase64
    ? base64ToBuf(saltBase64)
    : crypto.getRandomValues(new Uint8Array(16));

  const rawKey = await crypto.subtle.importKey(
    'raw',
    entropy,
    { name: 'PBKDF2' },
    false,
    ['deriveKey']
  );

  const key = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: saltBuf,
      iterations: 100000,
      hash: 'SHA-256',
    },
    rawKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );

  return { key, saltBase64: bufToBase64(saltBuf) };
}

/**
 * Enveloppe la MK avec une clé dérivée de la seed BIP39 (wrap #2).
 * Symétrique de wrapMasterKey(), mais AAD "safeheir-mk-seed-v1|" + user_id.
 * @param {Uint8Array} mkBytes - MK brute 32 bytes issue de generateMasterKey().
 * @param {CryptoKey} seedKey - Clé AES-GCM issue de deriveFromSeed().
 * @param {string} userId - user_id Supabase, liaison cryptographique.
 * @returns {Promise<{ ciphertext: string, iv: string }>} base64 — même forme
 *          que wrapMasterKey() : ciphertext → encrypted_mk_seed, iv → mk_seed_iv.
 */
export async function wrapWithSeed(mkBytes, seedKey, userId) {
  return wrapMasterKey(mkBytes, seedKey, AAD_MK_SEED_V1(userId));
}

/**
 * Désenveloppe la MK à partir d'une clé dérivée de la seed BIP39.
 * Symétrique de unwrapMasterKey().
 * @param {string} encryptedMKBase64 - Colonne encrypted_mk_seed.
 * @param {string} ivBase64 - Colonne mk_seed_iv.
 * @param {CryptoKey} seedKey - Clé AES-GCM issue de deriveFromSeed().
 * @param {string} userId - user_id Supabase, doit être celui du wrap.
 * @returns {Promise<CryptoKey>} MK importée, non-extractable.
 * @throws {DOMException} Si la seed, le sel, l'user_id ou le blob ne correspondent pas.
 */
export async function unwrapWithSeed(encryptedMKBase64, ivBase64, seedKey, userId) {
  return unwrapMasterKey(encryptedMKBase64, ivBase64, seedKey, AAD_MK_SEED_V1(userId));
}

// --- Chiffrement vault items ---

/**
 * Chiffre un objet JS en AES-256-GCM sous la Master Key.
 * @param {object} data - Objet à chiffrer (sera JSON.stringify).
 * @param {CryptoKey} key - Master Key issue de unwrapMasterKey().
 * @param {string} aad - Additional Authenticated Data.
 *                      Format SafeHeir vault : "safeheir-vault-item-v1|" + item_id
 * @returns {Promise<{ ciphertext: string, iv: string }>} base64.
 */
export async function encryptData(data, key, aad) {
  if (!aad || typeof aad !== 'string') {
    throw new Error('encryptData: aad parameter is required (string)');
  }
  return aesGcmEncrypt(new TextEncoder().encode(JSON.stringify(data)), key, aad);
}

/**
 * Déchiffre un ciphertext AES-256-GCM vers l'objet JS original.
 * @param {string} ciphertextBase64
 * @param {string} ivBase64
 * @param {CryptoKey} key
 * @param {string} aad - DOIT être identique à celui passé à encryptData().
 * @returns {Promise<object>}
 */
export async function decryptData(ciphertextBase64, ivBase64, key, aad) {
  if (!aad || typeof aad !== 'string') {
    throw new Error('decryptData: aad parameter is required (string)');
  }
  const plainbuf = await aesGcmDecrypt(ciphertextBase64, ivBase64, key, aad);
  return JSON.parse(new TextDecoder().decode(plainbuf));
}

// ===== Transmission (PF-03) — primitives =====

/**
 * Génère des octets aléatoires via le CSPRNG Web Crypto.
 * @param {number} length - Nombre d'octets (défaut 32, taille de K_trans).
 * @returns {Uint8Array}
 */
export function generateRandomBytes(length = 32) {
  return crypto.getRandomValues(new Uint8Array(length));
}

/**
 * Importe 32 octets bruts en clé AES-GCM 256 non-extractable.
 * @param {Uint8Array} bytes - Exactement 32 octets (ex. K_trans).
 * @returns {Promise<CryptoKey>} Usages encrypt/decrypt.
 * @throws {Error} Si bytes n'est pas un Uint8Array de 32 octets.
 */
export async function importAesKeyFromBytes(bytes) {
  assertBytes32(bytes, 'importAesKeyFromBytes');
  return crypto.subtle.importKey(
    'raw',
    bytes,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/**
 * Chiffre des octets bruts en AES-256-GCM (même convention que encryptData :
 * IV 12 octets aléatoire, tag GCM concaténé au ciphertext, base64).
 * @param {Uint8Array} bytes - Octets à chiffrer (longueur quelconque, 0 inclus).
 * @param {CryptoKey} key
 * @param {string} aad - Additional Authenticated Data, obligatoire non vide (DEC-017).
 * @returns {Promise<{ ciphertext: string, iv: string }>} base64.
 */
export async function encryptBytes(bytes, key, aad) {
  if (!aad || typeof aad !== 'string') {
    throw new Error('encryptBytes: aad parameter is required (string)');
  }
  if (!isUint8Array(bytes)) {
    throw new Error('encryptBytes: Uint8Array requis');
  }
  return aesGcmEncrypt(bytes, key, aad);
}

/**
 * Déchiffre un ciphertext produit par encryptBytes().
 * @param {string} ciphertextBase64
 * @param {string} ivBase64
 * @param {CryptoKey} key
 * @param {string} aad - DOIT être identique à celui passé à encryptBytes().
 * @returns {Promise<Uint8Array>}
 */
export async function decryptBytes(ciphertextBase64, ivBase64, key, aad) {
  if (!aad || typeof aad !== 'string') {
    throw new Error('decryptBytes: aad parameter is required (string)');
  }
  return aesGcmDecrypt(ciphertextBase64, ivBase64, key, aad);
}

/**
 * Partage additif 2-sur-2 d'un secret de 32 octets (spec §3.2, Annexe B).
 * serverShare = CSPRNG(32), holderShare = secret XOR serverShare.
 * Chaque part seule est indistinguable d'un aléa.
 * @param {Uint8Array} secret - 32 octets.
 * @returns {{ serverShare: Uint8Array, holderShare: Uint8Array }}
 */
export function splitSecret32(secret) {
  assertBytes32(secret, 'splitSecret32');
  const serverShare = generateRandomBytes(32);
  const holderShare = new Uint8Array(32);
  for (let i = 0; i < 32; i++) {
    holderShare[i] = secret[i] ^ serverShare[i];
  }
  return { serverShare, holderShare };
}

/**
 * Recombine deux parts additives : a XOR b.
 * @param {Uint8Array} a - 32 octets.
 * @param {Uint8Array} b - 32 octets.
 * @returns {Uint8Array} 32 octets.
 */
export function combineShares32(a, b) {
  assertBytes32(a, 'combineShares32');
  assertBytes32(b, 'combineShares32');
  const out = new Uint8Array(32);
  for (let i = 0; i < 32; i++) {
    out[i] = a[i] ^ b[i];
  }
  return out;
}

/**
 * Encode 32 octets en 24 mots BIP39, wordlist française (DEC-033).
 * @param {Uint8Array} bytes32
 * @returns {string} 24 mots séparés par des espaces.
 */
export function bytesToMnemonicFr(bytes32) {
  assertBytes32(bytes32, 'bytesToMnemonicFr');
  return entropyToMnemonic(bytes32, wordlist);
}

/**
 * Décode 24 mots BIP39 français en 32 octets. Tolère majuscules, espaces
 * en bord et espaces multiples. Distinct de deriveFromSeed() : aucune
 * dérivation, retour de l'entropie brute.
 * @param {string} phrase - 24 mots français.
 * @returns {Uint8Array} 32 octets.
 * @throws {Error} Si la phrase n'a pas 24 mots, contient un mot hors
 *                 wordlist française ou a un checksum faux.
 */
export function mnemonicFrToBytes(phrase) {
  if (typeof phrase !== 'string') {
    throw new Error('Code de 24 mots invalide (texte attendu)');
  }
  const normalized = phrase.trim().toLowerCase().split(/\s+/).join(' ');
  if (normalized.split(' ').length !== 24) {
    throw new Error('Code de 24 mots invalide (24 mots attendus)');
  }
  if (!validateMnemonic(normalized, wordlist)) {
    throw new Error('Code de 24 mots invalide (mots ou checksum incorrects)');
  }
  return mnemonicToEntropy(normalized, wordlist);
}
